import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DollarOutlined,
    LinkOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import {
    Table as AntTable,
    Button,
    Card,
    Col,
    Flex,
    Modal,
    Row,
    Tag,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { InvoiceDto, InvoiceShiftDto, ShiftType } from '@clh/api-client';
import { unwrapError } from '@clh/ui';
import {
    CostCalculator,
    calculateTotalStipend,
    HumlaDateTime,
    roundToAtMostTwoDecimals,
    toDollarAmount,
    toHourAndMinuteDisplay,
} from '@clh/util';

import { useApiClient } from '../hooks/use-api-client';
import { useToast } from '../hooks/use-toast';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';
import { summarizeTrueUpResults } from './true-up-summary';

const { Text } = Typography;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(amount / 100);

const shiftBonusCost = (shift: InvoiceShiftDto) =>
    CostCalculator.getTotalBonusCosts({
        bonuses: shift.bonuses ?? [],
        platformFeePct: shift.platformFeePct,
        employerFeePct: shift.employerFeePct,
    }).total;

const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'paid':
            return 'green';
        case 'open':
            return 'blue';
        case 'draft':
            return 'orange';
        case 'void':
        case 'uncollectible':
            return 'red';
        default:
            return 'default';
    }
};

const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
        case 'paid':
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        case 'open':
            return <DollarOutlined style={{ color: '#1890ff' }} />;
        case 'void':
        case 'uncollectible':
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        default:
            return null;
    }
};

function StripeApiDataComponent({
    invoice,
    totalCostPlatform,
}: {
    invoice: InvoiceDto;
    totalCostPlatform: number;
}) {
    return (
        <Card size="small" style={{ height: '100%' }}>
            <Flex
                justify="space-between"
                align="center"
                style={{ marginBottom: 16 }}
            >
                <Typography.Text strong style={{ color: '#fa8c16' }}>
                    STRIPE API DATA
                </Typography.Text>
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Text type="secondary">Invoice ID:</Text>
                    <div>
                        <Text code={true} copyable={true}>
                            {invoice.stripeInvoiceId}
                        </Text>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Status:</Text>
                    <div>
                        <Tag color={getStatusColor(invoice.status)}>
                            {invoice.status.toUpperCase()}
                        </Tag>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Total Cost:</Text>
                    <Flex align="center" gap={8}>
                        <div style={{ fontWeight: 500 }}>
                            {formatCurrency(invoice.totalCost)}
                        </div>
                        {invoice.totalCost === totalCostPlatform ? (
                            <CheckCircleOutlined
                                style={{ color: '#52c41a', fontSize: '16px' }}
                                title="Matches platform amount"
                            />
                        ) : (
                            <>
                                <CloseCircleOutlined
                                    style={{
                                        color: '#ff4d4f',
                                        fontSize: '16px',
                                    }}
                                    title="Does not match platform amount"
                                />
                                <Text
                                    type="danger"
                                    style={{ fontSize: '12px' }}
                                >
                                    Mismatch
                                </Text>
                            </>
                        )}
                    </Flex>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Invoice Date:</Text>
                    <div>
                        {DateTime.fromJSDate(
                            invoice.invoiceDate
                        ).toLocaleString(DateTime.DATE_SHORT)}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Due Date:</Text>
                    <div>
                        {invoice.dueDate
                            ? DateTime.fromJSDate(
                                  invoice.dueDate
                              ).toLocaleString(DateTime.DATE_SHORT)
                            : '\u2014'}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">PDF Link:</Text>
                    <div>
                        {invoice.pdfLink ? (
                            <Button
                                type="link"
                                size="small"
                                href={invoice.pdfLink}
                                target="_blank"
                                rel="noreferrer"
                                icon={<LinkOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                View PDF
                            </Button>
                        ) : (
                            <Text type="secondary">-</Text>
                        )}
                    </div>
                </Col>
            </Row>
        </Card>
    );
}

function InvoiceShiftDetails({
    shifts,
    totalCost,
}: {
    shifts: InvoiceShiftDto[];
    totalCost: number;
}) {
    return (
        <Flex justify="space-between">
            <AntTable<InvoiceShiftDto>
                size="small"
                rowKey="id"
                showHeader={true}
                pagination={false}
                footer={() => (
                    <Flex justify="end">
                        <div style={{ marginRight: 10 }}>
                            Total Cost: <br />
                            (platform)
                        </div>
                        <Typography.Title
                            level={5}
                            style={{ padding: 0, margin: 0 }}
                        >
                            {formatCurrency(totalCost)}
                        </Typography.Title>
                    </Flex>
                )}
                columns={[
                    {
                        dataIndex: 'facility',
                        title: 'Facility',
                        render: (facility, shift) => (
                            <Button
                                type="link"
                                target="_blank"
                                href={`/shifts/${shift.id}`}
                                icon={<LinkOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {facility.name}
                            </Button>
                        ),
                        ellipsis: true,
                    },
                    {
                        dataIndex: 'nurseProfile',
                        title: 'Nurse',
                        render: (nurseProfile) =>
                            nurseProfile && (
                                <Button
                                    type="link"
                                    target="_blank"
                                    href={`/profiles/${nurseProfile?.id}`}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    {nurseProfile.firstName}{' '}
                                    {nurseProfile.lastName}
                                </Button>
                            ),
                        ellipsis: true,
                    },
                    {
                        dataIndex: 'specialtyCode',
                        render: (_, shift) => (
                            <>
                                {shift.specialtyCode && (
                                    <Tag>{shift.specialtyCode}</Tag>
                                )}
                                <Tag>{shift.shiftType.toLowerCase()}</Tag>
                            </>
                        ),
                    },
                    {
                        dataIndex: 'startTime',
                        width: 320,
                        title: 'Scheduled time',
                        render: (_startTime, shift) => {
                            const tz = shift.facility?.timezone;
                            const start = DateTime.fromJSDate(shift.startTime);
                            const end = DateTime.fromJSDate(shift.endTime);
                            return (
                                <Typography.Text type="secondary">
                                    {(tz
                                        ? start.setZone(tz)
                                        : start
                                    ).toLocaleString(
                                        HumlaDateTime.DATETIME_SHORT
                                    )}{' '}
                                    -{' '}
                                    {(tz
                                        ? end.setZone(tz)
                                        : end
                                    ).toLocaleString(
                                        tz
                                            ? HumlaDateTime.DATETIME_SHORT_WITH_TZ
                                            : HumlaDateTime.DATETIME_SHORT
                                    )}
                                </Typography.Text>
                            );
                        },
                    },
                    {
                        title: 'Hours Worked',
                        dataIndex: 'minutesWorked',
                        render: (_, shift) => {
                            const totalMinutesWorked =
                                (shift.minutesWorked || 0) +
                                (shift.onCallMinutesWorked || 0);
                            return toHourAndMinuteDisplay(
                                totalMinutesWorked,
                                true
                            );
                        },
                    },
                    {
                        render: (_, shift) => {
                            const cellStyle = {
                                textAlign: 'center' as const,
                                lineHeight: '12px',
                                paddingLeft: 8,
                                paddingRight: 8,
                            };

                            const isOnCall =
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift;

                            return (
                                <Flex justify="end">
                                    <table style={{ width: 'auto' }}>
                                        <tbody>
                                            <tr>
                                                {isOnCall ? (
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.onCallShift!
                                                                    .minutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs on-call
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.onCallShift!
                                                                    .totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                on-call rate
                                                            </small>
                                                        </td>
                                                        <td>+</td>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.minutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs Called back
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                Called back rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.stipends.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        {shift.bonuses &&
                                                        shift.bonuses.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        shiftBonusCost(
                                                                            shift
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        bonus
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.minutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>hrs</small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                total rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.stipends.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        {shift.bonuses &&
                                                        shift.bonuses.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        shiftBonusCost(
                                                                            shift
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        bonus
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                )}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Flex>
                            );
                        },
                    },
                    {
                        dataIndex: 'totalRate',
                        title: 'Total Cost',
                        width: 100,
                        render: (_totalRate, shift) => {
                            let totalCost: number;

                            if (
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift
                            ) {
                                const onCallCost =
                                    (shift.onCallShift.totalRate *
                                        (shift.onCallShift.minutesWorked ||
                                            0)) /
                                    60;
                                const calledInCost =
                                    (shift.totalRate *
                                        (shift.minutesWorked || 0)) /
                                    60;
                                const stipend = calculateTotalStipend(
                                    shift.stipends?.filter((s) => s.qualified)
                                );
                                totalCost =
                                    onCallCost +
                                    calledInCost +
                                    stipend +
                                    shiftBonusCost(shift);
                            } else {
                                totalCost =
                                    (shift.totalRate *
                                        (shift.minutesWorked || 0)) /
                                        60 +
                                    calculateTotalStipend(
                                        shift.stipends?.filter(
                                            (s) => s.qualified
                                        )
                                    ) +
                                    shiftBonusCost(shift);
                            }

                            return <b>{formatCurrency(totalCost)}</b>;
                        },
                    },
                ]}
                dataSource={shifts}
            />
        </Flex>
    );
}

function platformTotalCost(shifts: InvoiceShiftDto[]) {
    return shifts.reduce(
        (total, shift) =>
            total +
            (shift.totalRate * (shift.minutesWorked || 0)) / 60 +
            calculateTotalStipend(shift.stipends?.filter((s) => s.qualified)) +
            shiftBonusCost(shift),
        0
    );
}

function InvoiceCard({ invoice }: { invoice: InvoiceDto }) {
    const totalCostPlatform = platformTotalCost(invoice.shifts);

    return (
        <Card
            key={invoice.stripeInvoiceId}
            size="small"
            style={{ marginBottom: 16 }}
        >
            <Flex
                justify="space-between"
                align="center"
                style={{ marginBottom: 16 }}
            >
                <Flex align="center" gap={8}>
                    {getStatusIcon(invoice.status)}
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        {invoice.facilityName}
                    </Typography.Title>
                </Flex>
                <Flex gap={8}>
                    <Button
                        href={`https://dashboard.stripe.com/invoices/${invoice.stripeInvoiceId}`}
                        target="_blank"
                        rel="noreferrer"
                        size="small"
                        icon={<LinkOutlined />}
                    >
                        Open in Stripe
                    </Button>
                </Flex>
            </Flex>

            <Card
                size="small"
                style={{
                    backgroundColor: '#f8f9ff',
                    marginBottom: 12,
                }}
            >
                <Typography.Text
                    strong
                    style={{
                        color: '#000',
                        marginBottom: 8,
                        display: 'block',
                    }}
                >
                    {invoice.shifts.length} Shift
                    {invoice.shifts.length !== 1 && 's'}
                </Typography.Text>
                <InvoiceShiftDetails
                    shifts={invoice.shifts}
                    totalCost={totalCostPlatform}
                />
            </Card>

            <StripeApiDataComponent
                invoice={invoice}
                totalCostPlatform={totalCostPlatform}
            />
        </Card>
    );
}

function TrueUpPreview({ pendingInvoices }: { pendingInvoices: InvoiceDto[] }) {
    if (!pendingInvoices.length) {
        return (
            <Text type="secondary">
                No facilities have uninvoiced shifts through this pay period.
            </Text>
        );
    }

    return (
        <>
            {pendingInvoices.map((pending) => (
                <Card
                    key={pending.shifts[0].facility.id}
                    size="small"
                    style={{ marginBottom: 12 }}
                >
                    <Typography.Text strong style={{ display: 'block' }}>
                        {pending.facilityName} — {pending.shifts.length} shift
                        {pending.shifts.length !== 1 && 's'}
                    </Typography.Text>
                    <InvoiceShiftDetails
                        shifts={pending.shifts}
                        totalCost={platformTotalCost(pending.shifts)}
                    />
                </Card>
            ))}
        </>
    );
}

function TrueUpInvoicesButton({ onComplete }: { onComplete: () => void }) {
    const api = useApiClient();
    const { notification } = useToast();
    const { endDate: payPeriodEnd } = usePayPeriodContext();
    const [pendingInvoices, setPendingInvoices] = useState<InvoiceDto[] | null>(
        null
    );
    const [previewLoading, setPreviewLoading] = useState(false);
    const [trueUpLoading, setTrueUpLoading] = useState(false);

    const openPreview = async () => {
        if (!api) return;

        setPreviewLoading(true);
        try {
            setPendingInvoices(
                await api.billingInvoiceControllerPreviewTrueUp({
                    payPeriodEnd: payPeriodEnd.toISOString(),
                })
            );
        } catch (error) {
            notification.error({ message: unwrapError(error), duration: 5 });
        } finally {
            setPreviewLoading(false);
        }
    };

    const runTrueUp = async () => {
        if (!api) return;

        setTrueUpLoading(true);
        try {
            const results = await api.billingInvoiceControllerTrueUpInvoices({
                payPeriodEnd: payPeriodEnd.toISOString(),
            });
            const { message, failed } = summarizeTrueUpResults(results);

            setPendingInvoices(null);
            onComplete();
            notification[failed ? 'warning' : 'success']({
                message,
                duration: failed ? 10 : 3,
            });
        } catch (error) {
            notification.error({ message: unwrapError(error), duration: 5 });
        } finally {
            setTrueUpLoading(false);
        }
    };

    return (
        <>
            <Button
                size="small"
                icon={<SyncOutlined />}
                loading={previewLoading}
                onClick={() => void openPreview()}
            >
                True up invoices
            </Button>
            <Modal
                open={pendingInvoices !== null}
                title="True up invoices"
                width={1100}
                okText={
                    pendingInvoices?.length
                        ? `Invoice ${pendingInvoices.length} ${
                              pendingInvoices.length === 1
                                  ? 'facility'
                                  : 'facilities'
                          }`
                        : 'Nothing to invoice'
                }
                okButtonProps={{ disabled: !pendingInvoices?.length }}
                confirmLoading={trueUpLoading}
                onOk={() => void runTrueUp()}
                onCancel={() => setPendingInvoices(null)}
            >
                <Text type="secondary" style={{ display: 'block' }}>
                    These shifts are not on any invoice yet and will be billed
                    through{' '}
                    {payPeriodEnd.toLocaleDateString('en-US', {
                        timeZone: 'UTC',
                    })}
                    .
                </Text>
                <TrueUpPreview pendingInvoices={pendingInvoices ?? []} />
            </Modal>
        </>
    );
}

function InvoicesSummary({
    invoices,
    onTrueUp,
}: {
    invoices: InvoiceDto[];
    onTrueUp: () => void;
}) {
    const totalInvoices = invoices.length;
    const totalShifts = invoices.reduce(
        (total, invoice) => total + invoice.shifts.length,
        0
    );
    const totalInvoiceAmount = invoices.reduce(
        (total, invoice) => total + invoice.totalCost,
        0
    );

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Flex align="center" gap={12}>
                    <Typography.Title
                        level={5}
                        style={{ margin: 0, padding: 0 }}
                    >
                        Invoices Summary
                    </Typography.Title>
                    <TrueUpInvoicesButton onComplete={onTrueUp} />
                </Flex>
                <Flex gap={24}>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#1890ff',
                            }}
                        >
                            {formatCurrency(totalInvoiceAmount)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Invoiced $
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#fa8c16',
                            }}
                        >
                            {totalInvoices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Invoices
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#52c41a',
                            }}
                        >
                            {totalShifts}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Shifts
                        </Text>
                    </div>
                </Flex>
            </Flex>
        </Card>
    );
}

export default function Invoices() {
    const api = useApiClient();
    const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
    const [loading, setLoading] = useState(false);

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchInvoices = async () => {
        if (!api) return;

        setLoading(true);
        try {
            const invoiceData = await api.billingInvoiceControllerGetInvoices({
                payPeriodStart: payPeriodStart.toISOString(),
                payPeriodEnd: payPeriodEnd.toISOString(),
            });

            setInvoices(invoiceData);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchInvoices();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            <InvoicesSummary
                invoices={invoices}
                onTrueUp={() => void fetchInvoices()}
            />
            {invoices.map((invoice) => (
                <InvoiceCard key={invoice.stripeInvoiceId} invoice={invoice} />
            ))}
        </>
    );
}
